/**
 * PUWYLO Homepage Expansion — feature killswitch helpers.
 *
 * Read off the global UFE config (SSR-safe — populated on both server and client):
 *  - `digitalPuwyloHomepageExpansion` — the feature killswitch (gates all FE behavior).
 *
 * The batch API call is gated by the feature killswitch AND the CMS per-component flag:
 * a recap card opts in via `personalization.isPuwyloBatchEnabled` (surfaced by CSX), and the
 * batch thunk collects those flagged contexts as `puwyloBatchContextIds` (see
 * `utils/localStorage/P13n.js` → `getContextIdsToUpdate`). The batch is fetched only when the
 * expansion killswitch is on and at least one card opted in.
 */

import constants from 'constants/content';
import Empty from 'constants/empty';
import RvDataActions from 'actions/RvDataActions';
import analyticsConstants from 'analytics/constants';
import { gateWithReason, PREFIX_CARD_DEALS } from 'utils/puwyloSunset/sunsetHelper';
import helpersUtils from 'utils/Helpers';
import localeUtils from 'utils/LanguageLocale';
import RecapEN from 'components/Content/Recap/locales/Recap_en_US';
import RecapFR from 'components/Content/Recap/locales/Recap_fr_CA';

const {
    RECAP_CAROUSEL: { ITEM_URLS }
} = constants;
const { readRvCategoryCookie } = RvDataActions;
const {
    CMS_COMPONENT_EVENTS: { PUWYLO_CONTROL_EXPERIENCE, PUWYLO_NO_TEST_EXPERIENCE, PUWYLO_SUNSET_SUPPRESSED }
} = analyticsConstants;

const KILLSWITCH_NAME = 'digitalPuwyloHomepageExpansion';

const getRecapText = localeUtils.getLocalisedDataFromResource(RecapEN, RecapFR);

const KEEP_SHOPPING_SOT_CARD_TYPE = 'keep_shopping';
const DEALS_SOT_CARD_TYPE = 'deals';
// Card 1 identity in the batch response (arch §2.3; distinct from the `keep_shopping`
// sotV453 value above). `withRecapListProps` derives `action.targetUrl = '/' + cardType`.
const PUWYLO_CARD1_CARD_TYPE = 'keep-shopping-for';
const PUWYLO_CARD2_CARD_TYPE = 'deals-shopping-for';
const MARKDOWNS_SOT_CARD_TYPE = 'markdowns';
const PUWYLO_CARD3_CARD_TYPE = 'new-markdowns-for-you';
// AB-test exclusion signal that marks a CONTROL-group Card 1 in `excludedCards[]`.
const AB_TEST_GROUP_CONTROL = 'CONTROL';
const AB_TEST_GROUP_CHALLENGER = 'CHALLENGER';
const AB_TEST_CONTROL_REASON = 'AB_TEST_CONTROL';
// Up to 4 SKUs on the wire (spike §5 / PDE-2737).
const PUWYLO_MAX_SOT_SKUS = 4;

function isPuwyloHomepageExpansionEnabled() {
    return Sephora?.configurationSettings?.[KILLSWITCH_NAME]?.isEnabled === true;
}

/**
 * PUWYLO Card 1 (Keep Shopping For): builds the card-specific SOT decoration —
 * `sotV453` (cardType), `sotV454` (`;`-joined skuIds, up to 4), the `sotV439` bag
 * (`abTestId;abTestEndDate;abTestTraffic;recentCategory;skuCount`) + mirrored `timestamp`, and the
 * `sotV342/343/344` AB-test trio sourced from the batch card's `abTest` block. Runs for all
 * auth states (signed-in AND anonymous/unrecognized — BR-001). Generic recap vars
 * (`sotV310/311/312/233/315/316/317`) already flow downstream and are not set here.
 *
 * @param {object} item - Recap item; must use `ITEM_URLS.KEEP_SHOPPING` and may carry `abTest`/`products`.
 * @returns {{ sotV453: string, sotV454: string, sotV439: string, timestamp: string, sotV342: boolean, sotV343: boolean, sotV344: boolean, personalization: object }|null} SOT patch, or `null` if not Card 1.
 */
function buildKeepShoppingForSotFields(item) {
    if (item?.action?.targetUrl !== ITEM_URLS.KEEP_SHOPPING) {
        return null;
    }

    const products = Array.isArray(item.products) ? item.products : Empty.Array;
    const skuIds = products
        .map(product => product?.skuId)
        .filter(Boolean)
        .slice(0, PUWYLO_MAX_SOT_SKUS);
    const skuCount = skuIds.length;

    const abTest = item.abTest || Empty.Object;
    const abTestId = abTest.isAbTest ? abTest.abTestId || '' : '';
    const abTestEndDate = abTest.abTestEndDate ?? '';
    // abTestTraffic (batch abTest block, e.g. 50) — gated on isAbTest like abTestId; '' when no test.
    const abTestTraffic = abTest.isAbTest ? (abTest.abTestTraffic ?? '') : '';
    const recentCategory = readRvCategoryCookie()[0] || '';

    const group = abTest.isAbTest ? (abTest.isControl ? AB_TEST_GROUP_CONTROL : AB_TEST_GROUP_CHALLENGER) : '';
    const keepShoppingSotV439Bundle =
        `abTestId:${abTestId};abTestEndDate:${abTestEndDate};abTestTraffic:${abTestTraffic};recentCategory:${recentCategory};skuCount:${skuCount}` +
        (group ? `;group:${group}` : '');

    return {
        sotV453: KEEP_SHOPPING_SOT_CARD_TYPE,
        sotV454: skuIds.join(';'),
        sotV439: keepShoppingSotV439Bundle,
        // banner.timestamp workaround, matches Replen / RTCO / Anonymous builders.
        timestamp: keepShoppingSotV439Bundle,
        sotV342: !!abTest.isDefault,
        sotV343: !!abTest.isControl,
        sotV344: !!abTest.isAbTest,
        personalization: {
            ...(item.personalization || Empty.Object),
            isDefault: !!abTest.isDefault,
            isControl: !!abTest.isControl,
            isAbTest: !!abTest.isAbTest,
            abTestEndDate: abTest.abTestEndDate || null
        }
    };
}

/**
 * Finds the Card 1 (Keep Shopping For) slot in a batch response list (`cards[]` or
 * `excludedCards[]`) by its `cardType`. SSR-safe; tolerant of non-array input.
 *
 * @param {Array} list normalized batch cards or raw excludedCards
 * @returns {object|undefined} the Card 1 entry, if present
 */
function findPuwyloCard1(list) {
    if (!Array.isArray(list)) {
        return undefined;
    }

    return list.find(entry => entry?.cardType === PUWYLO_CARD1_CARD_TYPE);
}

function resolvePuwyloCard1ContextId(response, requestedContextIds) {
    const card = findPuwyloCard1(response?.cards);
    const excluded = findPuwyloCard1(response?.excludedCards);

    return card?.contextId || excluded?.contextId || (Array.isArray(requestedContextIds) ? requestedContextIds[0] : '') || '';
}

function resolvePuwyloNoTestReason(card, excluded) {
    if (!card && !excluded) {
        return 'missing_context_id';
    }

    if (card && (!Array.isArray(card.products) || card.products.length === 0)) {
        return 'empty_products';
    }

    if (excluded) {
        return excluded.abTest?.isAbTest === true ? 'challenger_no_data' : 'no_test_no_data';
    }

    return 'missing_context_id';
}

// AB-test flag → SOT var mapping for the non-presentation payload. Named fields (not a positional
// trio) so the isDefault/isControl/isAbTest → sotV342/343/344 mapping is self-documenting and
// call sites can't silently transpose the flags.
function buildPuwyloBatchCard1SotFields({ ab, reason, flags }) {
    const abTestId = ab?.isAbTest ? ab.abTestId || '' : '';
    const abTestEndDate = ab?.abTestEndDate ?? '';
    // abTestTraffic (batch abTest block, e.g. 50) — gated on isAbTest like abTestId; '' when no test.
    const abTestTraffic = ab?.isAbTest ? (ab.abTestTraffic ?? '') : '';
    const recentCategory = readRvCategoryCookie()[0] || '';

    const group = flags.isAbTest ? (flags.isControl ? AB_TEST_GROUP_CONTROL : AB_TEST_GROUP_CHALLENGER) : '';
    const bag =
        `abTestId:${abTestId};abTestEndDate:${abTestEndDate};abTestTraffic:${abTestTraffic};recentCategory:${recentCategory};skuCount:0` +
        (group ? `;group:${group}` : '') +
        (reason ? `;reason:${reason}` : '');

    return {
        sotV453: KEEP_SHOPPING_SOT_CARD_TYPE,
        sotV454: '',
        sotV439: bag,
        // banner.timestamp workaround, matches Replen / RTCO / Anonymous builders.
        timestamp: bag,
        sotV342: flags.isDefault,
        sotV343: flags.isControl,
        sotV344: flags.isAbTest,
        action: { targetUrl: ITEM_URLS.KEEP_SHOPPING }
    };
}

function buildPuwyloBatchCard1SotEvent({ response } = {}) {
    const card = findPuwyloCard1(response?.cards);
    const excluded = findPuwyloCard1(response?.excludedCards);

    const isControl = card?.abTest?.isControl === true || excluded?.abTest?.isControl === true || excluded?.reason === AB_TEST_CONTROL_REASON;

    // Control precedence: a control slot always fires the control event, even with products.
    if (isControl) {
        const ab = card?.abTest ?? excluded?.abTest;

        return {
            sotType: PUWYLO_CONTROL_EXPERIENCE,
            sotFields: buildPuwyloBatchCard1SotFields({ ab, reason: null, flags: { isDefault: false, isControl: true, isAbTest: true } })
        };
    }

    // Renderable challenger card (products present, not control) → no new event.
    if (card && Array.isArray(card.products) && card.products.length > 0) {
        return null;
    }

    // No-test experience: challenger-no-data, no-test-no-data, empty products, or missing context.
    const ab = card?.abTest ?? excluded?.abTest;
    const isChallenger = ab?.isAbTest === true && ab?.isControl !== true;
    const flags = isChallenger ? { isDefault: false, isControl: false, isAbTest: true } : { isDefault: true, isControl: false, isAbTest: false };
    const reason = resolvePuwyloNoTestReason(card, excluded);

    return {
        sotType: PUWYLO_NO_TEST_EXPERIENCE,
        sotFields: buildPuwyloBatchCard1SotFields({ ab, reason, flags })
    };
}

/**
 * Builds SOT field overrides for a Card 2 (Deals) recap item. Returns `null` for non-Deals items.
 * Mirrors `buildKeepShoppingForSotFields` — sotV454 carries up to `PUWYLO_MAX_SOT_SKUS` SKU IDs
 * and sotV439 encodes AB-test metadata plus the displayed SKU's pricing.
 *
 * @param {object} item - Recap item; must use `ITEM_URLS.DEALS` and may carry `abTest`/`skus`.
 * @returns {{ sotV453: string, sotV454: string, sotV439: string, timestamp: string, sotV342: boolean, sotV343: boolean, sotV344: boolean }|null} SOT patch, or `null` if not Card 2.
 */
function buildDealsSotFields(item) {
    if (item?.action?.targetUrl !== ITEM_URLS.DEALS) {
        return null;
    }

    const skus = Array.isArray(item.skus) ? item.skus : Empty.Array;
    const skuIds = skus
        .map(sku => sku?.skuId)
        .filter(Boolean)
        .slice(0, PUWYLO_MAX_SOT_SKUS);

    const displayedSku = skus[0] || Empty.Object;

    const abTest = item.abTest || Empty.Object;
    const abTestId = abTest.abTestId ?? '';
    const abTestEndDate = abTest.abTestEndDate ?? '';
    const abTestTraffic = abTest.abTestTraffic ?? '';
    const skuId = displayedSku.skuId ?? '';
    const listPrice = displayedSku.listPrice ?? '';
    const salePrice = displayedSku.salePrice ?? '';

    const hasAbTest = Boolean(item.abTest);
    const isDefault = hasAbTest ? Boolean(abTest.isDefault) : true;
    const isControl = hasAbTest ? Boolean(abTest.isControl) : false;
    const isAbTest = hasAbTest ? Boolean(abTest.isAbTest) : false;

    const group = abTest.isAbTest ? (abTest.isControl ? AB_TEST_GROUP_CONTROL : AB_TEST_GROUP_CHALLENGER) : '';
    const dealsSotV439Bundle =
        `abTestId:${abTestId};abTestEndDate:${abTestEndDate};abTestTraffic:${abTestTraffic};skuId:${skuId};listPrice:${listPrice};salePrice:${salePrice}` +
        (group ? `;group:${group}` : '');

    return {
        sotV453: DEALS_SOT_CARD_TYPE,
        sotV454: skuIds.join(';'),
        sotV439: dealsSotV439Bundle,
        timestamp: dealsSotV439Bundle,
        sotV342: isDefault,
        sotV343: isControl,
        sotV344: isAbTest,
        personalization: {
            ...(item.personalization || Empty.Object),
            isDefault,
            isControl,
            isAbTest,
            abTestEndDate: abTestEndDate || null
        }
    };
}

/**
 * Finds the Card 2 (Deals) slot in a batch response list (`cards[]` or `excludedCards[]`)
 * by its `cardType`. SSR-safe; tolerant of non-array input.
 *
 * @param {Array} list normalized batch cards or raw excludedCards
 * @returns {object|undefined} the Card 2 entry, if present
 */
function findPuwyloCard2(list) {
    if (!Array.isArray(list)) {
        return undefined;
    }

    return list.find(entry => entry?.cardType === PUWYLO_CARD2_CARD_TYPE);
}

function buildPuwyloBatchCard2SotFields({ ab, skuIds }) {
    const abTestId = ab?.abTestId ?? '';
    const abTestEndDate = ab?.abTestEndDate ?? '';
    const abTestTraffic = ab?.abTestTraffic ?? '';

    const group = ab?.isAbTest ? (ab?.isControl ? AB_TEST_GROUP_CONTROL : AB_TEST_GROUP_CHALLENGER) : '';
    const bag =
        `abTestId:${abTestId};abTestEndDate:${abTestEndDate};abTestTraffic:${abTestTraffic};skuId:;listPrice:;salePrice:` +
        (group ? `;group:${group}` : '');

    return {
        sotV453: DEALS_SOT_CARD_TYPE,
        sotV454: skuIds ? skuIds.join(';') : '',
        sotV439: bag,
        timestamp: bag,
        sotV342: !!ab?.isDefault,
        sotV343: !!ab?.isControl,
        sotV344: !!ab?.isAbTest,
        action: { targetUrl: ITEM_URLS.DEALS }
    };
}

/**
 * Resolves the context ID for Card 2 (Deals) using a 3-level fallback chain:
 * `cards` → `excludedCards` → `requestedContextIds[1]` → `''`.
 *
 * @param {object} response batch API response (`{ cards, excludedCards }`)
 * @param {string[]} requestedContextIds CMS-configured context IDs sent in the batch request
 * @returns {string} the resolved context ID, or `''` when none is available
 */
function resolvePuwyloCard2ContextId(response, requestedContextIds) {
    const card = findPuwyloCard2(response?.cards);
    const excluded = findPuwyloCard2(response?.excludedCards);

    return card?.contextId || excluded?.contextId || (Array.isArray(requestedContextIds) ? requestedContextIds[1] : '') || '';
}

/**
 * Determines and builds the non-presentation SOT event descriptor for Card 2 (Deals)
 * from the batch API response. Returns `null` when the card is renderable (products present,
 * not all sunset-suppressed, not control) — the presentation-path impression handles that case.
 *
 * Decision tree: control → PUWYLO_CONTROL_EXPERIENCE; renderable + all SKUs sunset-suppressed →
 * PUWYLO_SUNSET_SUPPRESSED; renderable (not suppressed) → null; no card/excluded → null;
 * otherwise → PUWYLO_NO_TEST_EXPERIENCE with challenger/default flags.
 *
 * @param {object} params
 * @param {object} params.response batch API response (`{ cards, excludedCards }`)
 * @returns {{ sotType: string, sotFields: object }|null} event descriptor, or `null` for renderable cards
 */
function buildPuwyloBatchCard2SotEvent({ response } = {}) {
    const card = findPuwyloCard2(response?.cards);
    const excluded = findPuwyloCard2(response?.excludedCards);

    const isControl = card?.abTest?.isControl === true || excluded?.abTest?.isControl === true;

    if (isControl) {
        const ab = card?.abTest ?? excluded?.abTest;
        const products = card?.products ?? excluded?.products ?? [];
        const skuIds = products.map(p => p?.skuId).filter(Boolean);

        return {
            sotType: PUWYLO_CONTROL_EXPERIENCE,
            sotFields: buildPuwyloBatchCard2SotFields({ ab, skuIds })
        };
    }

    if (card && Array.isArray(card.products) && card.products.length > 0) {
        const productSkuIds = card.products.map(p => p?.skuId).filter(Boolean);
        const gateResults = productSkuIds.map(skuId => gateWithReason(skuId, PREFIX_CARD_DEALS));
        const allSuppressed = productSkuIds.length > 0 && gateResults.every(r => r.verdict === 'HIDE');

        if (allSuppressed) {
            const sunsetTrigger = gateResults[0]?.trigger || '';
            const ab = card?.abTest;
            const sotFields = buildPuwyloBatchCard2SotFields({ ab, skuIds: productSkuIds });
            sotFields.sotV439 = sotFields.sotV439 + `;sunset_trigger:${sunsetTrigger}`;
            sotFields.timestamp = sotFields.sotV439;

            return { sotType: PUWYLO_SUNSET_SUPPRESSED, sotFields };
        }

        return null;
    }

    if (!card && !excluded) {
        return null;
    }

    const ab = card?.abTest ?? excluded?.abTest;
    const isChallenger = ab?.isAbTest === true && ab?.isControl !== true;
    const flags = isChallenger ? { isDefault: false, isControl: false, isAbTest: true } : { isDefault: true, isControl: false, isAbTest: false };

    return {
        sotType: PUWYLO_NO_TEST_EXPERIENCE,
        sotFields: {
            ...buildPuwyloBatchCard2SotFields({ ab, skuIds: [] }),
            sotV342: flags.isDefault,
            sotV343: flags.isControl,
            sotV344: flags.isAbTest
        }
    };
}

/**
 * PUWYLO Card 3 (New Markdowns for You): builds the card-specific SOT decoration —
 * `sotV453` (cardType), `sotV454` (`;`-joined skuIds, up to 4), the `sotV439` bag
 * (`abTestId;abTestEndDate;abTestTraffic;skuCount` + optional `group`) + mirrored `timestamp`, and
 * the `sotV342/343/344` AB-test trio sourced from the item's `abTest` block, mirrored onto
 * `personalization` (the field the downstream Launch rule actually reads for the trio). Mirrors
 * `buildKeepShoppingForSotFields` (Card 1) — same `item.products` source and same bag shape, minus
 * `recentCategory` (an RV-cookie value specific to Keep Shopping). No per-SKU pricing is reported.
 *
 * @param {object} item - Recap item; must use `ITEM_URLS.NEW_MARKDOWNS` and may carry `abTest`/`products`.
 * @returns {{ sotV453: string, sotV454: string, sotV439: string, timestamp: string, sotV342: boolean, sotV343: boolean, sotV344: boolean, personalization: object }|null} SOT patch, or `null` if not Card 3.
 */
function buildNewMarkdownsSotFields(item) {
    if (item?.action?.targetUrl !== ITEM_URLS.NEW_MARKDOWNS) {
        return null;
    }

    const products = Array.isArray(item.products) ? item.products : Empty.Array;
    const skuIds = products
        .map(product => product?.skuId)
        .filter(Boolean)
        .slice(0, PUWYLO_MAX_SOT_SKUS);
    const skuCount = skuIds.length;

    const abTest = item.abTest || Empty.Object;
    const abTestId = abTest.isAbTest ? abTest.abTestId || '' : '';
    const abTestEndDate = abTest.abTestEndDate ?? '';
    // abTestTraffic (batch abTest block, e.g. 50) — gated on isAbTest like abTestId; '' when no test.
    const abTestTraffic = abTest.isAbTest ? (abTest.abTestTraffic ?? '') : '';

    const group = abTest.isAbTest ? (abTest.isControl ? AB_TEST_GROUP_CONTROL : AB_TEST_GROUP_CHALLENGER) : '';
    const newMarkdownsSotV439Bundle =
        `abTestId:${abTestId};abTestEndDate:${abTestEndDate};abTestTraffic:${abTestTraffic};skuCount:${skuCount}` + (group ? `;group:${group}` : '');

    return {
        sotV453: MARKDOWNS_SOT_CARD_TYPE,
        sotV454: skuIds.join(';'),
        sotV439: newMarkdownsSotV439Bundle,
        timestamp: newMarkdownsSotV439Bundle,
        sotV342: item.abTest ? !!abTest.isDefault : true,
        sotV343: item.abTest ? !!abTest.isControl : false,
        sotV344: item.abTest ? !!abTest.isAbTest : false,
        personalization: {
            ...(item.personalization || Empty.Object),
            isDefault: item.abTest ? !!abTest.isDefault : true,
            isControl: item.abTest ? !!abTest.isControl : false,
            isAbTest: item.abTest ? !!abTest.isAbTest : false,
            abTestEndDate: abTest.abTestEndDate || null
        }
    };
}

/**
 * Finds the Card 3 (New Markdowns for You) slot in a batch response list (`cards[]` or
 * `excludedCards[]`) by its `cardType`. SSR-safe; tolerant of non-array input.
 *
 * @param {Array} list normalized batch cards or raw excludedCards
 * @returns {object|undefined} the Card 3 entry, if present
 */
function findPuwyloCard3(list) {
    if (!Array.isArray(list)) {
        return undefined;
    }

    return list.find(entry => entry?.cardType === PUWYLO_CARD3_CARD_TYPE);
}

/**
 * Builds the SOT field bag for a Card 3 (New Markdowns) non-presentation event (control or
 * no-test). Produces `sotV453` (card type), `sotV454` (`;`-joined SKU IDs), the `sotV439` bundle
 * (`abTestId;abTestEndDate;abTestTraffic;skuCount`), and the `sotV342/343/344` AB-test trio.
 *
 * @param {object} params
 * @param {object} params.ab the card's `abTest` block (may be undefined)
 * @param {string[]} params.skuIds product SKU IDs to report in `sotV454`
 * @returns {object} SOT field patch consumed by `firePuwyloBatchCardSotEvent`
 */
function buildPuwyloBatchCard3SotFields({ ab, skuIds }) {
    const abTestId = ab?.isAbTest ? ab.abTestId || '' : '';
    const abTestEndDate = ab?.abTestEndDate ?? '';
    const abTestTraffic = ab?.isAbTest ? (ab.abTestTraffic ?? '') : '';

    const group = ab?.isAbTest ? (ab?.isControl ? AB_TEST_GROUP_CONTROL : AB_TEST_GROUP_CHALLENGER) : '';
    const bag =
        `abTestId:${abTestId};abTestEndDate:${abTestEndDate};abTestTraffic:${abTestTraffic};skuCount:${skuIds?.length || 0}` +
        (group ? `;group:${group}` : '');

    return {
        sotV453: MARKDOWNS_SOT_CARD_TYPE,
        sotV454: skuIds ? skuIds.join(';') : '',
        sotV439: bag,
        timestamp: bag,
        sotV342: !!ab?.isDefault,
        sotV343: !!ab?.isControl,
        sotV344: !!ab?.isAbTest,
        action: { targetUrl: ITEM_URLS.NEW_MARKDOWNS }
    };
}

/**
 * Resolves the context ID for Card 3 (New Markdowns for You) using a 3-level fallback chain:
 * `cards` → `excludedCards` → `requestedContextIds[2]` → `''`.
 *
 * @param {object} response batch API response (`{ cards, excludedCards }`)
 * @param {string[]} requestedContextIds CMS-configured context IDs sent in the batch request
 * @returns {string} the resolved context ID, or `''` when none is available
 */
function resolvePuwyloCard3ContextId(response, requestedContextIds) {
    const card = findPuwyloCard3(response?.cards);
    const excluded = findPuwyloCard3(response?.excludedCards);

    return card?.contextId || excluded?.contextId || (Array.isArray(requestedContextIds) ? requestedContextIds[2] : '') || '';
}

/**
 * Determines and builds the non-presentation SOT event descriptor for Card 3 (New Markdowns for
 * You) from the batch API response. Returns `null` when the card is renderable (products present,
 * not control) — the presentation-path impression handles that case instead, or when neither
 * `cards` nor `excludedCards` carries the card. No sunset suppression branch (Card 2-specific).
 *
 * Decision tree: control → PUWYLO_CONTROL_EXPERIENCE; renderable → null;
 * otherwise → PUWYLO_NO_TEST_EXPERIENCE with challenger/default flags.
 *
 * @param {object} params
 * @param {object} params.response batch API response (`{ cards, excludedCards }`)
 * @returns {{ sotType: string, sotFields: object }|null} event descriptor, or `null` for renderable cards
 */
function buildPuwyloBatchCard3SotEvent({ response } = {}) {
    const card = findPuwyloCard3(response?.cards);
    const excluded = findPuwyloCard3(response?.excludedCards);

    const isControl = card?.abTest?.isControl === true || excluded?.abTest?.isControl === true;

    if (isControl) {
        const ab = card?.abTest ?? excluded?.abTest;
        const products = card?.products ?? excluded?.products ?? [];
        const skuIds = products.map(p => p?.skuId).filter(Boolean);

        return {
            sotType: PUWYLO_CONTROL_EXPERIENCE,
            sotFields: buildPuwyloBatchCard3SotFields({ ab, skuIds })
        };
    }

    if (card && Array.isArray(card.products) && card.products.length > 0) {
        return null;
    }

    if (!card && !excluded) {
        return null;
    }

    const ab = card?.abTest ?? excluded?.abTest;
    const isChallenger = ab?.isAbTest === true && ab?.isControl !== true;
    const flags = isChallenger ? { isDefault: false, isControl: false, isAbTest: true } : { isDefault: true, isControl: false, isAbTest: false };

    return {
        sotType: PUWYLO_NO_TEST_EXPERIENCE,
        sotFields: {
            ...buildPuwyloBatchCard3SotFields({ ab, skuIds: [] }),
            sotV342: flags.isDefault,
            sotV343: flags.isControl,
            sotV344: flags.isAbTest
        }
    };
}

/**
 * Selects the PUWYLO batch cards eligible to display in the carousel: excludes cards
 * with no products (no empty slots) and Control Group cards (abTest.isControl=true)
 * while preserving the order received from the batch API. The backend returns cards
 * in the Contentful-configured order (BR-020), so the frontend renders them as-received
 * and does NOT re-sort. Pure + SSR-safe; never mutates the input array.
 *
 * @param {Array} cards normalized batch cards ({ contextId, displayOrder, products, abTest, ... })
 * @returns {Array} filtered copy in received order
 */
function getEligiblePuwyloCards(cards) {
    if (!Array.isArray(cards)) {
        return [];
    }

    return cards.filter(card => {
        const hasProducts = Array.isArray(card?.products) && card.products.length > 0;
        const isControl = card?.abTest?.isControl === true && card?.abTest?.isAbTest === true;

        // Exclude Control Group cards (they fire impression separately)
        return hasProducts && !isControl;
    });
}

/**
 * Decides whether the enhanced PUWYLO carousel should render. True only when the
 * killswitch is on AND `batchData` is non-null AND at least one card survives
 * the empty-product filter. Otherwise false → existing carousel / fallback path.
 *
 * @param {Object|null} batchData normalized batch payload or null fallback sentinel
 * @returns {boolean}
 */
function shouldUsePuwyloCarousel(batchData) {
    if (!isPuwyloHomepageExpansionEnabled() || !batchData) {
        return false;
    }

    return getEligiblePuwyloCards(batchData.cards).length > 0;
}

/**
 * @param {object} sku hero SKU carrying `salePrice`/`listPrice`
 * @param {boolean} showPercentOffFlag resolved AB-test flag from the selector
 * @returns {{ shouldShow: boolean, label: string }} badge visibility + localized label
 */
function getPuwyloPercentOffBadge(sku, showPercentOffFlag) {
    const { percentageOff, isRange } = helpersUtils.calculatePercentageOff(sku?.salePrice, sku?.listPrice) || {};
    const shouldShow = Boolean(showPercentOffFlag && localeUtils.isCanada() && percentageOff);

    return {
        shouldShow,
        label: shouldShow ? getRecapText(isRange ? 'percentageOffRange' : 'percentageOff', [percentageOff]) : ''
    };
}

/**
 *
 * @param {object} sku hero SKU carrying `listPrice`/`salePrice`/`valuePrice`
 * @param {string} percentOffLabel already-localized badge label ('' when hidden)
 * @returns {string} comma-joined aria-label
 */
function getPuwyloDealPriceAriaLabel(sku, percentOffLabel) {
    return [getRecapText('priceAriaLabel', [sku?.listPrice, sku?.salePrice]), percentOffLabel || '', sku?.valuePrice || '']
        .filter(Boolean)
        .join(', ');
}

export default {
    isPuwyloHomepageExpansionEnabled,
    getEligiblePuwyloCards,
    shouldUsePuwyloCarousel,
    buildKeepShoppingForSotFields,
    buildDealsSotFields,
    buildNewMarkdownsSotFields,
    buildPuwyloBatchCard1SotEvent,
    buildPuwyloBatchCard2SotEvent,
    buildPuwyloBatchCard3SotEvent,
    findPuwyloCard2,
    findPuwyloCard3,
    resolvePuwyloCard1ContextId,
    resolvePuwyloCard2ContextId,
    resolvePuwyloCard3ContextId,
    getPuwyloPercentOffBadge,
    getPuwyloDealPriceAriaLabel
};
